<script lang="ts" setup>
const log = useLogger('component:CheckCustomFormNative')

interface ModifierOption {
  id: string
  name: string
  price_change: number
  is_default: boolean
}

interface _ProductModifier {
  id: string
  name: string
  is_multiple: boolean
  is_required: boolean
  number_of_options: number
  options: ModifierOption[]
  default_option: ModifierOption[]
}

interface ModifierSelection {
  id: string
  name: string
  price_modifier: number
  quantity?: number
}

interface OrderItem {
  group_id: number
  product_id: number
  product_name: string
  quantity: number
  selected_modifiers?: {
    [modifierId: string]: ModifierSelection | ModifierSelection[]
  }
}

// Using any type for comboCustomOptions from store

// Removed ComboCustomOptions interface as it's not used with storeToRefs

const { comboCustomOptions } = storeToRefs(usePosCheckStore())
const { t } = useI18n()

// Track which sections are open
const openGroups = ref<Set<number>>(new Set())
const openProducts = ref<Set<string>>(new Set())

// Track if this is the initial load
const isInitialLoad = ref(true)

// Lifecycle hooks to track component mounting/unmounting
onMounted(() => {
  console.log('🔄 CheckCustomFormNative mounted')
})

onUnmounted(() => {
  console.log('❌ CheckCustomFormNative unmounted')
})

// Initialize state when modal opens (no auto-opening)
watchEffect(() => {
  console.log('⚠️ watchEffect triggered - isInitialLoad:', isInitialLoad.value)
  try {
    if (comboCustomOptions.value?.items && Array.isArray(comboCustomOptions.value.items)) {
      // Only clear state on initial load
      if (isInitialLoad.value) {
        console.log('🧹 Clearing openGroups (initial load)')
        openGroups.value.clear()
        openProducts.value.clear()
        isInitialLoad.value = false
      }

      // Don't auto-open any accordions - let users open only what they need to customize

      // Ensure selected products have their default modifiers set
      comboCustomOptions.value.items.forEach((group: any) => {
        if (group.selected && group.selected.product_id) {
          // Find the actual product in options
          const selectedProduct = group.options?.find((opt: any) =>
            opt.product_id === group.selected.product_id,
          )

          // If product has modifiers and selected doesn't have selected_modifiers, set defaults
          if (selectedProduct?.product_modifiers?.length > 0
            && (!group.selected.selected_modifiers || Object.keys(group.selected.selected_modifiers).length === 0)) {
            // Setting missing default modifiers
            setDefaultModifiers(group.selected, selectedProduct.product_modifiers)
          }
        }
      })
    }
  }
  catch (error) {
    log.error('Error initializing sections:', error)
  }
})

function toggleGroup(groupId: number) {
  console.log('🔄 toggleGroup called with groupId:', groupId)
  console.log('Current openGroups:', Array.from(openGroups.value))

  try {
    if (!groupId && groupId !== 0) return

    if (openGroups.value.has(groupId)) {
      console.log('❌ Closing group:', groupId)
      openGroups.value.delete(groupId)
    }
    else {
      console.log('✅ Opening group:', groupId)
      openGroups.value.add(groupId)

      // Auto-expand if group has only one product option or if a product is already selected
      const group = comboCustomOptions.value?.items?.find((g: any) => g.group_id === groupId)
      if (group) {
        // Check if only one product option exists
        const hasOnlyOneOption = group.options?.length === 1
        // Check if a product is already selected
        const hasSelectedProduct = group.selected?.product_id

        if (hasOnlyOneOption || hasSelectedProduct) {
          // Determine which product to auto-expand
          const productToExpand = hasSelectedProduct
            ? group.options?.find((opt: any) => opt.product_id === group.selected.product_id)
            : group.options?.[0]

          if (productToExpand) {
            const productKey = `${groupId}_${productToExpand.product_id}`
            openProducts.value.add(productKey)
          }
        }
      }
    }
  }
  catch (error) {
    log.error('Error toggling group:', error)
    useErrorToast('Unable to expand section')
  }
}

function toggleProduct(groupId: number, productId: number) {
  try {
    if ((!groupId && groupId !== 0) || (!productId && productId !== 0)) return

    const key = `${groupId}_${productId}`
    if (openProducts.value.has(key)) {
      openProducts.value.delete(key)
    }
    else {
      openProducts.value.add(key)

      // Product accordion opened
    }
  }
  catch (error) {
    log.error('Error toggling product:', error)
    useErrorToast('Unable to expand product details')
  }
}

function selectProductOption(option: any, opt: any) {
  console.log('🎯 selectProductOption called')
  console.log('Option:', option)
  console.log('Opt:', opt)
  console.log('Current openGroups before selection:', Array.from(openGroups.value))

  try {
    if (!option || !opt) {
      log.error('Invalid option or opt parameter')
      return
    }

    // Clear all selections for this combo group first (only one product per group)
    if (comboCustomOptions.value?.items && Array.isArray(comboCustomOptions.value.items)) {
      comboCustomOptions.value.items.forEach((group: any) => {
        if (group?.group_id === option.group_id) {
          // Clear previous selection in this group
          group.selected = null
        }
      })
    }

    option.selected = {
      group_id: option.group_id || 0,
      product_id: opt.product_id || 0,
      product_name: opt.product_name || '',
      quantity: opt.quantity || 1,
      price_variation: opt.price_variation || 0,
      selected_modifiers: {},
    }

    // Set default modifiers if product has unified modifiers
    if (opt.product_modifiers?.length > 0) {
      setDefaultModifiers(option.selected, opt.product_modifiers)
    }

    // Open this product's section and close others in the same group
    const groupProducts = option.options || []
    groupProducts.forEach((p: any) => {
      if (!p?.product_id) return
      const key = `${option.group_id}_${p.product_id}`
      if (p.product_id === opt.product_id) {
        openProducts.value.add(key)
      }
      else {
        openProducts.value.delete(key)
      }
    })

    console.log('✅ Product selected successfully')
    console.log('openGroups after selection:', Array.from(openGroups.value))
  }
  catch (error) {
    log.error('Error selecting product option:', error)
    useErrorToast('Unable to select product')
  }
}

function setDefaultModifiers(selected: OrderItem, productModifiers: any[]) {
  if (!selected.selected_modifiers) {
    selected.selected_modifiers = {}
  }

  productModifiers.forEach((modifier) => {
    // First check if modifier has default_option array
    if (modifier.default_option?.length > 0) {
      // Always use arrays for consistency
      selected.selected_modifiers![modifier.id] = modifier.default_option.map((opt: any) => ({
        id: opt.id,
        name: opt.name,
        price_modifier: opt.price_change || 0,
        quantity: 1,
      }))
    }
    // Also check for is_default flag on individual options
    else if (modifier.options?.length > 0) {
      const defaultOptions = modifier.options.filter((opt: any) => opt.is_default === true)
      if (defaultOptions.length > 0) {
        // For single selection modifiers, take only the first default
        if (!modifier.is_multiple && defaultOptions.length > 0) {
          selected.selected_modifiers![modifier.id] = [{
            id: defaultOptions[0].id,
            name: defaultOptions[0].name,
            price_modifier: defaultOptions[0].price_change || 0,
            quantity: 1,
          }]
        }
        // For multiple selection modifiers, include all defaults
        else {
          selected.selected_modifiers![modifier.id] = defaultOptions.map((opt: any) => ({
            id: opt.id,
            name: opt.name,
            price_modifier: opt.price_change || 0,
            quantity: 1,
          }))
        }
      }
    }
  })
}

function selectModifierOption(selected: OrderItem, modifier: any, modifierOption: any) {
  console.log('🔧 selectModifierOption called')
  console.log('Selected:', selected)
  console.log('Modifier:', modifier)
  console.log('ModifierOption:', modifierOption)
  console.log('Current openGroups before modifier selection:', Array.from(openGroups.value))

  try {
    if (!selected) {
      useErrorToast('Please select an item to continue')
      return
    }

    if (!modifier?.id || !modifierOption?.id) {
      log.error('Invalid modifier or modifier option')
      return
    }

    if (!selected.selected_modifiers) {
      selected.selected_modifiers = {}
    }

    const selection: ModifierSelection = {
      id: modifierOption.id,
      name: modifierOption.name || '',
      price_modifier: modifierOption.price_change || 0,
      quantity: 1,
    }

    // Always use arrays for consistency
    if (!selected.selected_modifiers[modifier.id]) {
      selected.selected_modifiers[modifier.id] = []
    }
    const currentSelections = selected.selected_modifiers[modifier.id] as ModifierSelection[]

    if (modifier.is_multiple) {
      // Multiple selection modifier (like toppings) - toggle behavior
      const existingIndex = currentSelections.findIndex(s => s.id === modifierOption.id)
      if (existingIndex >= 0) {
        // Toggle off - remove selection
        currentSelections.splice(existingIndex, 1)
      }
      else {
        // Add new selection
        currentSelections.push(selection)
      }
    }
    else {
      // Single selection modifier (like size) - replace array content
      selected.selected_modifiers[modifier.id] = [selection]
    }

    console.log('✅ Modifier selected successfully')
    console.log('openGroups after modifier selection:', Array.from(openGroups.value))
  }
  catch (error) {
    log.error('Error selecting modifier option:', error)
    useErrorToast('Unable to select modifier')
  }
}

function isModifierOptionSelected(selected: OrderItem, modifier: any, modifierOption: any): boolean {
  if (!selected?.selected_modifiers?.[modifier.id]) {
    return false
  }

  const selections = selected.selected_modifiers[modifier.id] as ModifierSelection[]

  // Always expect arrays now
  const isSelected = Array.isArray(selections) && selections.some(s => s.id === modifierOption.id)

  return isSelected
}
</script>

<template>
  <div
    v-if="comboCustomOptions?.items"
    class="custom-form-native"
  >
    <!-- Main combo groups -->
    <div
      v-for="option in comboCustomOptions.items"
      :key="option.group_id"
      class="group-section mb-2"
    >
      <div
        :id="`group-header-${option.group_id}`"
        class="group-header"
        :class="{ 'group-open': openGroups.has(option.group_id) }"
        :aria-expanded="openGroups.has(option.group_id).toString()"
        :aria-controls="`group-content-${option.group_id}`"
        role="button"
        tabindex="0"
        @click="toggleGroup(option.group_id)"
        @keydown.enter="toggleGroup(option.group_id)"
        @keydown.space.prevent="toggleGroup(option.group_id)"
      >
        <div class="group-title">
          <span class="group-name">{{ option.group_name }}</span>
          <span
            v-if="option.selected"
            class="selected-product"
          >
            - {{ option.selected.quantity }} x {{ option.selected.product_name }}
          </span>
        </div>
        <div
          class="expand-icon"
          aria-hidden="true"
        >
          <span v-if="openGroups.has(option.group_id)">▼</span>
          <span v-else>▶</span>
        </div>
      </div>

      <!-- Group content -->
      <div
        v-show="openGroups.has(option.group_id)"
        :id="`group-content-${option.group_id}`"
        class="group-content"
        role="region"
        :aria-labelledby="`group-header-${option.group_id}`"
      >
        <!-- Product options -->
        <div
          v-for="opt in option.options"
          :key="`${option.group_id}_${opt.product_id}`"
          class="product-section"
        >
          <div
            class="product-header"
            :class="{
              'product-selected': opt?.product_id === option?.selected?.product_id,
              'product-open': openProducts.has(`${option.group_id}_${opt.product_id}`),
            }"
          >
            <div class="product-info">
              <button
                class="select-button"
                :class="{
                  selected: opt?.product_id === option?.selected?.product_id,
                }"
                :aria-pressed="(opt?.product_id === option?.selected?.product_id).toString()"
                :aria-label="`${opt?.product_id === option?.selected?.product_id ? 'Selected' : 'Select'} ${opt.quantity} x ${opt.product_name}`"
                @click.stop="console.log('🖱️ Select button clicked'); selectProductOption(option, opt)"
              >
                {{ opt?.product_id === option?.selected?.product_id ? '✓' : '+' }}
              </button>
              <div
                class="product-details"
                role="button"
                tabindex="0"
                :aria-label="`${openProducts.has(`${option.group_id}_${opt.product_id}`) ? 'Collapse' : 'Expand'} ${opt.product_name} options`"
                @click.stop="opt.product_modifiers && opt.product_modifiers.length > 0 && opt?.product_id === option?.selected?.product_id && toggleProduct(option.group_id, opt.product_id)"
                @keydown.enter="opt.product_modifiers && opt.product_modifiers.length > 0 && opt?.product_id === option?.selected?.product_id && toggleProduct(option.group_id, opt.product_id)"
                @keydown.space.prevent="opt.product_modifiers && opt.product_modifiers.length > 0 && opt?.product_id === option?.selected?.product_id && toggleProduct(option.group_id, opt.product_id)"
              >
                <span
                  v-if="opt?.quantity"
                  class="product-quantity"
                >{{ opt.quantity }} x </span>
                <span
                  v-if="opt?.product_name"
                  class="product-name"
                >{{ opt.product_name }}</span>
                <span
                  v-if="opt?.price_variation"
                  class="price-variation"
                >
                  + {{ usePriceFormatter(opt.price_variation) }}
                </span>
              </div>
            </div>
            <button
              v-if="opt.product_modifiers && opt.product_modifiers.length > 0 && opt?.product_id === option?.selected?.product_id"
              class="expand-hint"
              :aria-expanded="openProducts.has(`${option.group_id}_${opt.product_id}`).toString()"
              :aria-controls="`modifiers-${option.group_id}_${opt.product_id}`"
              :aria-label="`${openProducts.has(`${option.group_id}_${opt.product_id}`) ? 'Collapse' : 'Expand'} customization options for ${opt.product_name}`"
              @click.stop="toggleProduct(option.group_id, opt.product_id)"
            >
              <span class="hint-text">{{ t('Expand to customize') }}</span>
              <span
                class="expand-icon-small"
                aria-hidden="true"
              >
                <span v-if="openProducts.has(`${option.group_id}_${opt.product_id}`)">▼</span>
                <span v-else>▶</span>
              </span>
            </button>
          </div>

          <!-- Product modifiers - only show for selected product -->
          <div
            v-if="opt?.product_modifiers?.length > 0 && opt?.product_id === option?.selected?.product_id"
            v-show="openProducts.has(`${option.group_id}_${opt.product_id}`)"
            :id="`modifiers-${option.group_id}_${opt.product_id}`"
            class="modifiers-content"
            role="region"
            :aria-label="`Customization options for ${opt.product_name}`"
          >
            <div
              v-for="modifier in opt.product_modifiers"
              :key="modifier.id"
              class="modifier-group"
            >
              <h3 class="modifier-title">
                {{ modifier.name }}
                <span
                  v-if="modifier.is_required"
                  class="required"
                >*</span>
                <span
                  v-if="modifier.is_multiple"
                  class="multiple"
                >(Multiple)</span>
              </h3>
              <div class="modifier-options">
                <button
                  v-for="modifierOption in modifier.options"
                  :key="modifierOption.id"
                  class="modifier-button"
                  :class="{
                    'modifier-selected': option?.selected && isModifierOptionSelected(option.selected, modifier, modifierOption),
                  }"
                  @click.stop="console.log('🖱️ Modifier button clicked'); option?.selected && selectModifierOption(option.selected, modifier, modifierOption)"
                >
                  <span class="modifier-name">
                    {{ modifierOption.name }}
                    <span
                      v-if="modifierOption.is_default"
                      class="default-indicator"
                    >(Default)</span>
                  </span>
                  <span
                    v-if="modifierOption.price_change > 0"
                    class="modifier-price"
                  >
                    +{{ usePriceFormatter(modifierOption.price_change) }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Base styles */
.custom-form-native {
  padding: 0.5rem;
}

/* Group section styles */
.group-section {
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
  transition: all 0.3s ease;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  min-height: 60px; /* Ensure minimum touch target height */
  background-color: #f9fafb;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.group-header:hover {
  background-color: #f3f4f6;
}

.group-header:focus {
  outline: 3px solid #3b82f6;
  outline-offset: -3px;
}

.group-header.group-open {
  background-color: #e0f2fe;
  border-bottom: 2px solid #0ea5e9;
}

.group-title {
  font-size: 1.125rem;
  font-weight: 600;
}

.group-name {
  color: #1f2937;
}

.selected-product {
  font-size: 1rem;
  font-weight: 400;
  color: #6b7280;
  margin-left: 0.5rem;
}

.expand-icon {
  color: #6b7280;
  transition: transform 0.2s ease;
  padding: 0.5rem;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Group content animation */
.group-content {
  animation: slideDown 0.3s ease-out;
  padding: 0.5rem;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Product section styles */
.product-section {
  margin-bottom: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  overflow: hidden;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background-color: white;
  transition: all 0.2s ease;
}

.product-header:hover {
  background-color: #f9fafb;
}

.product-header.product-selected {
  background-color: #dcfce7;
}

.product-header.product-open {
  border-bottom: 1px solid #e5e7eb;
}

.product-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.select-button {
  min-width: 44px;
  min-height: 44px;
  width: 44px;
  height: 44px;
  border-radius: 0.375rem;
  border: 1px solid #d1d5db;
  background-color: #f3f4f6;
  color: #4b5563;
  font-weight: 600;
  font-size: 1.125rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.select-button:hover {
  background-color: #e5e7eb;
}

.select-button:focus {
  outline: 3px solid #3b82f6;
  outline-offset: 2px;
}

.select-button.selected {
  background-color: #10b981;
  color: white;
  border-color: #10b981;
}

.product-details {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  user-select: none;
}

/* Make product details clickable when it has modifiers */
.product-details[role="button"] {
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s ease;
}

.product-details[role="button"]:hover {
  background-color: #f3f4f6;
}

.product-details[role="button"]:focus {
  outline: 3px solid #3b82f6;
  outline-offset: 2px;
}

.product-quantity,
.product-name {
  font-weight: 600;
}

.price-variation {
  color: #1f2937;
  font-size: 0.875rem;
}

.expand-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  min-height: 44px;
  cursor: pointer;
  user-select: none;
  background: none;
  border: none;
  border-radius: 0.375rem;
  transition: background-color 0.2s ease;
}

.expand-hint:hover {
  background-color: #f3f4f6;
}

.expand-hint:focus {
  outline: 3px solid #3b82f6;
  outline-offset: 2px;
}

.hint-text {
  font-size: 0.875rem;
  color: #6b7280;
}

.expand-icon-small {
  font-size: 0.75rem;
  color: #6b7280;
}

/* Modifiers content */
.modifiers-content {
  padding: 1rem;
  background-color: #f9fafb;
  animation: slideDown 0.3s ease-out;
}

.modifier-group {
  margin-bottom: 1rem;
}

.modifier-group:last-child {
  margin-bottom: 0;
}

.modifier-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.required {
  color: #ef4444;
  margin-left: 0.125rem;
}

.multiple {
  color: #3b82f6;
  font-size: 0.75rem;
  margin-left: 0.25rem;
}

.modifier-options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.5rem;
}

@media (min-width: 768px) {
  .modifier-options {
    grid-template-columns: repeat(3, 1fr);
  }
}

.modifier-button {
  padding: 0.75rem;
  min-height: 60px;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background-color: white;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.25rem;
}

.modifier-button:hover {
  background-color: #f3f4f6;
  border-color: #9ca3af;
}

.modifier-button:focus {
  outline: 3px solid #3b82f6;
  outline-offset: 2px;
}

.modifier-button.modifier-selected {
  background-color: #fb923c;
  border-color: #f97316;
  color: white;
}

.modifier-name {
  font-size: 0.875rem;
  font-weight: 500;
}

.modifier-price {
  font-size: 0.75rem;
  color: #1f2937;
}

.modifier-button.modifier-selected .modifier-price {
  color: white;
}

.default-indicator {
  font-size: 0.65rem;
  font-weight: normal;
  color: #6b7280;
  margin-left: 0.25rem;
}

.modifier-button.modifier-selected .default-indicator {
  color: rgba(255, 255, 255, 0.8);
}
</style>
